import { useEffect, useMemo, useState } from "react";
import { useRemoveVideoFromAllCampaigns, useRemoveVideoFromCampaign, useVideoCampaigns } from "@/api/video-campaigns";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { GmvMaxCampaignTag } from "@/components/assurance/badges/ad-campaign-badge";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { error, success } from "@/providers/ToastProvider";
import { pluralize } from "@/utils/text";

const GMV_MAX_CAMPAIGN_TYPE = "GMV_MAX";

interface ManageCampaignsModalProps {
  videoId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ManageCampaignsModal = ({ videoId, open, onOpenChange }: ManageCampaignsModalProps) => {
  const { data, isLoading } = useVideoCampaigns(open ? videoId : undefined);
  const { mutateAsync: removeFromCampaign, isPending: isRemovingSingle } = useRemoveVideoFromCampaign();
  const { mutateAsync: removeFromAll, isPending: isRemovingAll } = useRemoveVideoFromAllCampaigns();
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<string[]>([]);

  const isRemoving = isRemovingSingle || isRemovingAll;

  const uniqueCampaigns = useMemo(() => {
    // Excluded GMV Max campaigns are surfaced (and cancellable) in the video details view, not here.
    const campaigns = (data?.campaigns ?? []).filter((c) => !c.isExcluded);
    return campaigns.reduce(
      (acc, campaign) => {
        if (!acc.find((c) => c.campaignId === campaign.campaignId)) {
          acc.push({ ...campaign });
        }
        return acc;
      },
      [] as typeof campaigns,
    );
  }, [data?.campaigns]);

  const allCampaignIds = useMemo(() => uniqueCampaigns.map((c) => c.campaignId), [uniqueCampaigns]);

  useEffect(() => {
    if (uniqueCampaigns.length > 0) {
      setSelectedCampaignIds(uniqueCampaigns.map((c) => c.campaignId));
    }
  }, [uniqueCampaigns]);

  const uncheckedIds = allCampaignIds.filter((id) => !selectedCampaignIds.includes(id));
  const hasChanges = uncheckedIds.length > 0;

  const handleToggleCampaign = (campaignId: string) => {
    setSelectedCampaignIds((prev) =>
      prev.includes(campaignId) ? prev.filter((id) => id !== campaignId) : [...prev, campaignId],
    );
  };

  const handleToggleAll = () => {
    if (selectedCampaignIds.length === uniqueCampaigns.length) {
      setSelectedCampaignIds([]);
    } else {
      setSelectedCampaignIds(allCampaignIds);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasChanges) return;

    const removingAll = uncheckedIds.length === uniqueCampaigns.length;

    if (removingAll) {
      try {
        await removeFromAll({ videoId });
        success("Video successfully removed from all campaigns.");
        onOpenChange(false);
      } catch {
        error("Failed to remove video from campaigns.");
      }
    } else {
      const results = await Promise.allSettled(
        uncheckedIds.map((campaignId) => removeFromCampaign({ videoId, campaignId })),
      );

      const failed = results.filter((r) => r.status === "rejected").length;
      const succeeded = results.length - failed;

      if (failed === 0) {
        success(
          succeeded === 1
            ? "Video successfully removed from campaign."
            : `Video successfully removed from ${pluralize(succeeded, "campaign", "campaigns")}.`,
        );
      } else {
        error(
          failed === results.length
            ? "Failed to remove video from campaigns."
            : `Failed to remove video from ${pluralize(failed, "campaign", "campaigns")}.`,
        );
      }

      onOpenChange(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setSelectedCampaignIds([]);
    }
    onOpenChange(newOpen);
  };

  const allSelected = selectedCampaignIds.length === uniqueCampaigns.length && uniqueCampaigns.length > 0;
  const someSelected = selectedCampaignIds.length > 0 && !allSelected;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[34rem]">
        <form onSubmit={handleSubmit} className="space-y-6">
          <DialogHeader>
            <DialogTitle>Manage Ad campaigns</DialogTitle>
          </DialogHeader>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingIndicator />
            </div>
          ) : (
            <div className="flex flex-col max-h-[24rem] overflow-auto rounded-xl border border-border-secondary">
              {/* Table header */}
              <div className="flex items-center gap-3 px-4 py-3 border-b border-border-secondary bg-secondary sticky top-0 rounded-t-xl">
                <Checkbox
                  isSelected={allSelected}
                  isDisabled={uniqueCampaigns.length === 0 || isRemoving}
                  aria-label="Select all campaigns"
                  className={someSelected ? "data-[state=checked]:bg-gray-400" : ""}
                  onClick={(e) => {
                    e.preventDefault();
                    handleToggleAll();
                  }}
                />
                <span className="text-sm text-tertiary">Campaign name ↓</span>
              </div>

              {uniqueCampaigns.length === 0 ? (
                <div className="relative flex items-center justify-center py-16 px-8">
                  <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,228,230,0.6)_0%,rgba(255,241,230,0.4)_50%,rgba(240,235,255,0.3)_100%)] rounded-b-xl" />
                  <div className="relative text-center space-y-1">
                    <p className="text-sm font-semibold text-primary">No Campaign set up</p>
                    <p className="text-sm text-tertiary">
                      Please go to TikTok and create a campaign to populate this list.
                    </p>
                  </div>
                </div>
              ) : (
                uniqueCampaigns.map((campaign) => {
                  const isSelected = selectedCampaignIds.includes(campaign.campaignId);
                  return (
                    <button
                      key={campaign.campaignId}
                      type="button"
                      className={`flex items-center gap-3 px-4 py-4 border-b last:border-b-0 cursor-pointer w-full text-left transition-colors ${
                        isSelected
                          ? "border-brand-solid bg-brand-primary_alt"
                          : "border-border-secondary hover:bg-secondary/50"
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleCampaign(campaign.campaignId);
                      }}
                      disabled={isRemoving}
                    >
                      <Checkbox
                        isSelected={isSelected}
                        isDisabled={isRemoving}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleCampaign(campaign.campaignId);
                        }}
                      />
                      <span className="text-sm text-primary truncate">{campaign.campaignName}</span>
                      {campaign.campaignType === GMV_MAX_CAMPAIGN_TYPE && <GmvMaxCampaignTag />}
                    </button>
                  );
                })
              )}
            </div>
          )}

          <DialogFooter>
            <Button color="secondary" size="lg" onClick={() => handleOpenChange(false)} disabled={isRemoving}>
              Cancel
            </Button>
            <Button
              color="primary"
              size="lg"
              type="submit"
              isDisabled={!hasChanges || isRemoving}
              isLoading={isRemoving}
            >
              Save changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
